import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  useAssignTaskMutation,
  useTeamsShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import { assignModalSchema } from '../schema';
import { useState } from 'react';
import { t } from 'i18next';
import { formatApiError } from '@common/errors/error-formatting.ts';

const initialValues: FormShape = {
  type: 'team',
};

interface FormShape {
  type: string;
  team_id?: string;
  user_id?: string;
  assign_to_self?: boolean;
  assign_to_assigned_cm?: boolean;
}

export const useAssignModal = (task_id: string) => {
  const assignTaskMutation = useAssignTaskMutation(task_id);

  const { data: teamsShortOptions = [] } = useTeamsShortQuery({});
  const { data: usersShortOptions = [] } = useUserListShortQuery({});

  const [isOpen, setIsOpen] = useState(false);

  const assignTypeOptions = [
    {
      value: 'team',
      label: t('worklist.worklistItem.assignModal.teamLabel'),
    },
    {
      value: 'user',
      label: t('worklist.worklistItem.assignModal.userLabel'),
    },
    {
      value: 'self',
      label: t('worklist.worklistItem.assignModal.selfLabel'),
    },
    {
      value: 'assignedCm',
      label: t('worklist.worklistItem.assignModal.assignedCm'),
    },
  ];

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const formMethods = useForm<FormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<FormShape, any, any>(assignModalSchema),
    defaultValues: initialValues,
  });

  const { handleSubmit } = formMethods;

  const submitHandler = async (values: FormShape) => {
    try {
      await assignTaskMutation.mutateAsync({
        ...(values.type === 'team' && { team_id: values.team_id }),
        ...(values.type === 'user' && { user_id: values.user_id }),
        ...(values.type === 'self' && { assign_to_self: true }),
        ...(values.type === 'assignedCm' && { assign_to_assigned_cm: true }),
      });

      toast.success(t('worklist.worklistItem.assignModal.successMessage'));
      handleClose();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  return {
    onSubmit,
    formMethods,
    isOpen,
    handleOpen,
    handleClose,
    loading: assignTaskMutation.isPending,
    usersShortOptions,
    teamsShortOptions: teamsShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    assignTypeOptions,
  };
};
